import ChevronRight from '@mui/icons-material/ChevronRight';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { buttonBaseClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { KeyboardEvent, MouseEvent } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';

export type JobTimesheetRowState =
  | 'locked'
  | 'matches'
  | 'noTimes'
  | 'reported'
  | 'scanning';

interface ReportedValue {
  key: string;
  label: string;
  value: string | null;
}

interface JobTimesheetRowProps {
  actionColumnWidthInPx: number;
  hasReportedTimes: boolean;
  isApplying: boolean;
  label: string;
  onDelete?: () => void;
  onOpen: () => void;
  onUseTimesheetHours?: () => void;
  previewUrl: string | null;
  reportedValues: ReportedValue[];
  state: JobTimesheetRowState;
}

const NO_CONTENT = '-';
// Sized to sit level with the slip label beside it rather than to the image's own ratio.
const SLIP_THUMBNAIL_SIZE_IN_PX = 20;
// Leaves a small ring of the circle visible around the icon inside the thumbnail slot.
const DOCUMENT_ICON_SIZE_IN_PX = 13;

export default function JobTimesheetRow({
  actionColumnWidthInPx,
  hasReportedTimes,
  isApplying,
  label,
  onDelete,
  onOpen,
  onUseTimesheetHours,
  previewUrl,
  reportedValues,
  state,
}: JobTimesheetRowProps) {
  const theme = useTheme();
  const { colors } = theme.brandV2;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  // The whole row opens the timesheet, so its own controls must not also trigger that.
  const stopRowClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Box
      aria-label={label}
      data-test-id="job-timesheet-row"
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      sx={{
        alignItems: 'center',
        // Same card chrome as the project form's repeatable material rows (FormArrayRowCard).
        border: `solid 1px ${colors.treadGray7}`,
        borderLeft: `solid 4px ${colors.treadYellow}`,
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        gap: 3,
        px: 1.5,
        py: 1,
        '&:hover': { bgcolor: colors.treadGray9 },
        '&:focus-visible': { outline: `solid 2px ${colors.treadBlue}`, outlineOffset: 2 },
      }}
    >
      {/* Mirrors the stat row's own proportions so each reported value starts on the same
          column boundary as the job figure it annotates. */}
      <Box
        sx={{
          display: 'flex',
          flex: 4,
          gap: 3,
          justifyContent: 'space-between',
          minWidth: 0,
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1, minWidth: 0 }}>
          {previewUrl ? (
            <Box
              alt=""
              component="img"
              src={previewUrl}
              sx={{
                border: `solid 1px ${colors.treadGray7}`,
                borderRadius: 0.5,
                flex: 'none',
                height: `${SLIP_THUMBNAIL_SIZE_IN_PX}px`,
                objectFit: 'cover',
                width: `${SLIP_THUMBNAIL_SIZE_IN_PX}px`,
              }}
            />
          ) : (
            <Box
              data-test-id="timesheet-without-image-icon"
              sx={{
                alignItems: 'center',
                bgcolor: colors.treadGray8,
                borderRadius: '50%',
                display: 'flex',
                flex: 'none',
                height: `${SLIP_THUMBNAIL_SIZE_IN_PX}px`,
                justifyContent: 'center',
                width: `${SLIP_THUMBNAIL_SIZE_IN_PX}px`,
              }}
            >
              <DescriptionOutlined
                sx={{
                  color: colors.treadGray3,
                  fontSize: `${DOCUMENT_ICON_SIZE_IN_PX}px`,
                }}
              />
            </Box>
          )}

          <Typography
            sx={{
              color: colors.treadBlue,
              fontSize: '0.8125rem',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
        </Box>

        {hasReportedTimes &&
          reportedValues.map((reported) => (
            <Box
              key={reported.key}
              sx={{
                alignItems: 'baseline',
                display: 'flex',
                flex: 1,
                gap: 0.625,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: colors.treadGray1,
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {reported.label}
              </Typography>

              <Typography
                sx={{ color: colors.treadBlack, fontSize: '0.8125rem', fontWeight: 600 }}
              >
                {reported.value ?? NO_CONTENT}
              </Typography>
            </Box>
          ))}
      </Box>

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 'none',
          gap: 1,
          justifyContent: 'flex-end',
          width: `${actionColumnWidthInPx}px`,
        }}
      >
        {state === 'scanning' && (
          <Typography
            sx={{
              border: `solid 1px ${colors.treadOrangeDark}`,
              borderRadius: 0.5,
              color: colors.treadOrangeDark,
              fontSize: '0.6875rem',
              fontWeight: 600,
              px: 0.625,
            }}
          >
            {t('approvals.timesheets.status_processing')}
          </Typography>
        )}

        {onUseTimesheetHours && (
          <SmallButton
            color="inherit"
            disabled={isApplying}
            onClick={(event) => {
              stopRowClick(event);
              onUseTimesheetHours();
            }}
            variant="text"
            sx={{
              [`&.${buttonBaseClasses.root}`]: {
                color: colors.treadBlue,
                fontWeight: 600,
                height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
              },
            }}
          >
            {t('approvals.approve_jobs.driver_reported.use_timesheet_times')}
          </SmallButton>
        )}

        {onDelete && (
          <IconButton
            aria-label={t('actions.delete')}
            onClick={(event) => {
              stopRowClick(event);
              onDelete();
            }}
            size="small"
            sx={{ color: colors.treadRed }}
          >
            <DeleteOutline sx={{ fontSize: '18px' }} />
          </IconButton>
        )}

        <ChevronRight
          aria-hidden
          data-test-id="job-timesheet-row-open-icon"
          sx={{ color: colors.treadGray4, flex: 'none', fontSize: '18px' }}
        />
      </Box>
    </Box>
  );
}
